/**
 * Task 3.7 — duration formatter for the per-member time row.
 *
 * Renders "Xh Ym" / "Xh" / "Ym" between the two pickers. Uses the project's
 * shared dayjs instance (with utc + timezone plugins already extended) so
 * tz-aware diff matches the validation hook.
 *
 * Display format of the pickers themselves stays project-default — that's
 * driven by `DateTimeInput`'s internal `dateFormat` lookup from `useGlobalStore`
 * (see `src/components/input/DateTimeInput.tsx` L66-67), so this module only
 * has to worry about the duration label.
 *
 * Empty / invalid inputs return `"00:00"` so the row UI always has a label.
 */

import { dayjs } from "utilz";

export const formatDuration = (
  startIso: string | null,
  endIso: string | null,
  timezone?: string,
): string => {
  if (!startIso || !endIso) {
    return "00:00";
  }

  const start = timezone ? dayjs(startIso).tz(timezone) : dayjs(startIso);
  const end = timezone ? dayjs(endIso).tz(timezone) : dayjs(endIso);

  if (!start.isValid() || !end.isValid()) {
    return "00:00";
  }

  const diffMinutes = end.diff(start, "minute");

  // Negative or zero duration → fall back to a flat "00:00". The validation
  // hook surfaces the "Invalid time range" error separately; the duration
  // label deliberately doesn't display "-1h".
  if (diffMinutes <= 0) {
    return "00:00";
  }

  const hours = Math.floor(diffMinutes / 60);
  const minutes = diffMinutes % 60;

  if (hours > 0 && minutes > 0) {
    return `${hours}h ${minutes}m`;
  }
  if (hours > 0) {
    return `${hours}h`;
  }
  return `${minutes}m`;
};
